/**
 * InputWatchdog.ts — Monitors for stuck input states.
 *
 * Detects when keys are pressed but no valid input is processed,
 * indicating a soft-lock. After thresholds are exceeded, shows a
 * recovery prompt to return to the title screen.
 */

import type { Terminal } from './Terminal';
import type { InputManager, InputEvent } from './InputManager';

export class InputWatchdog {
  private keypressesSinceLastValid = 0;
  private lastValidInputTime = Date.now();
  private readonly STUCK_THRESHOLD_KEYS = 10;
  private readonly STUCK_THRESHOLD_MS = 15_000;
  private recoveryCallback: (() => void) | null = null;
  private active = true;

  constructor(
    private terminal: Terminal,
    private inputManager: InputManager,
  ) {}

  /** Call when any keypress is detected (from InputManager) */
  onKeypress(): void {
    if (!this.active) return;
    this.keypressesSinceLastValid++;

    if (
      this.keypressesSinceLastValid >= this.STUCK_THRESHOLD_KEYS ||
      Date.now() - this.lastValidInputTime > this.STUCK_THRESHOLD_MS
    ) {
      this.showRecoveryPrompt();
    }
  }

  /** Call when a valid input is processed (menu selection, text submit, anyKey) */
  onValidInput(): void {
    this.keypressesSinceLastValid = 0;
    this.lastValidInputTime = Date.now();
  }

  /** Set callback for recovery action (typically: engine.resetTo(new TitleScene())) */
  setRecoveryCallback(cb: () => void): void {
    this.recoveryCallback = cb;
  }

  /** Temporarily disable (during transitions) */
  disable(): void {
    this.active = false;
  }

  enable(): void {
    this.active = true;
    this.reset();
  }

  private reset(): void {
    this.keypressesSinceLastValid = 0;
    this.lastValidInputTime = Date.now();
  }

  private showRecoveryPrompt(): void {
    this.reset(); // Prevent re-triggering
    const { rows, cols } = this.terminal.getSize();
    const row = Math.floor(rows / 2) - 1;
    const msg = ' Stuck? Press Enter to return to title. ';
    const col = Math.floor((cols - msg.length) / 2);

    this.terminal.setCursor(row, col);
    this.terminal.write(msg, 'yellow');

    // Listen for Enter to trigger recovery
    const handler = (event: InputEvent) => {
      if (event.type === 'anyKey' || event.type === 'menuSelect' || event.type === 'textSubmit') {
        this.inputManager.off(handler);
        if (this.recoveryCallback) this.recoveryCallback();
      }
    };
    this.inputManager.on(handler);
  }
}
